import type {
  BookAttributesV1,
  BookLanguage,
  BookPlanId,
  StandardPageFormat,
} from '@kitup/book-schema';
import { request } from './client.js';

export interface StyleDto {
  readonly id: string;
  readonly name: string;
  readonly previewAssetId: string | null;
  readonly isSystem: boolean;
  readonly order: number;
}

/** A row of `GET /books`: the book plus its front cover, signed for display. */
export interface BookListItemDto extends BookDto {
  readonly coverUrl: string | null;
}

export interface BookDto {
  readonly id: string;
  /** Internal working name shown in the owner's book library. */
  readonly title: string;
  /** Reader-facing title approved with the story and printed on the cover. */
  readonly coverTitle: string | null;
  readonly subtitle: string | null;
  readonly dedication: string | null;
  readonly language: BookLanguage;
  readonly styleId: string;
  readonly style: StyleDto;
  readonly styleLockedAt: string | null;
  /** Не `null` — книга открыта по публичной ссылке и правки запрещены. */
  readonly publishedAt: string | null;
  readonly status: 'draft' | 'generating' | 'ready' | 'archived';
  readonly plan: BookPlanId;
  readonly paidAt: string | null;
  readonly paidPriceMinorUnits: number | null;
  readonly refrain: string | null;
  readonly targetAgeYears: number | null;
  readonly occasion: string | null;
  readonly mustInclude: readonly string[];
  readonly mustAvoid: readonly string[];
  readonly sceneCount: number;
  readonly hasStoryDraft: boolean;
  readonly coverAssetId: string | null;
  readonly backCoverAssetId: string | null;
  readonly schemaVersion: number;
  readonly attributes: BookAttributesV1;
  readonly composition: {
    readonly characters: number;
    readonly things: number;
    readonly locations: number;
  };
  readonly layoutImpact: Readonly<Record<'split' | 'full-bleed' | 'band', number>>;
  readonly sceneSummary: {
    readonly total: number;
    readonly ready: number;
    readonly generated: number;
    readonly videos: number;
    readonly narrated: number;
    readonly stale: number;
  };
  readonly hasExport: boolean;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface UpdateBookInput {
  readonly styleId?: string;
  readonly title?: string;
  readonly subtitle?: string | null;
  readonly styleName?: string;
  readonly targetAgeYears?: number | null;
  readonly occasion?: string | null;
  readonly plan?: BookPlanId;
}

export const booksApi = {
  list: () => request<{ items: BookListItemDto[] }>('/books'),
  get: (bookId: string) => request<BookDto>(`/books/${bookId}`),
  styles: () => request<{ items: StyleDto[] }>('/styles'),
  create: (body: { title: string; language: BookLanguage; printSize: StandardPageFormat }) =>
    request<BookDto>('/books', { method: 'POST', body }),
  update: (bookId: string, body: UpdateBookInput) =>
    request<BookDto>(`/books/${bookId}`, { method: 'PATCH', body }),
  updateAttributes: (bookId: string, attributes: BookAttributesV1, confirmStaleImages: boolean) =>
    request<BookDto>(`/books/${bookId}/attributes`, {
      method: 'PATCH',
      body: { attributes, confirmStaleImages },
    }),
  archive: (bookId: string) =>
    request<{ id: string; status: 'archived' }>(`/books/${bookId}/archive`, {
      method: 'POST',
    }),
  duplicate: (bookId: string, styleId?: string) =>
    request<BookDto>(`/books/${bookId}/duplicate`, {
      method: 'POST',
      body: styleId ? { styleId } : {},
    }),
};
